// FILE: components/VirtualizedTable.tsx
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Dropdown } from './Dropdown';
import { RawCheckbox } from './Checkbox';
import { Icon } from './icons';

// Enhanced sorting configuration
interface SortConfig {
    key: any;
    direction: 'asc' | 'desc';
    priority: number; // 1 = primary, 2 = secondary, 3 = tertiary
}

// Custom sort function type
type CustomSortFunction<T> = (a: T, b: T, direction: 'asc' | 'desc') => number;

// Virtualized Table with all advanced features
interface VirtualizedTableProps<T> {
    data: T[];
    columns: {
        key: any;
        header: string;
        sortable?: boolean;
        visible?: boolean;
        width?: number;
        textWrap?: 'wrap' | 'nowrap' | 'ellipsis'; // Text wrapping control
        className?: string; // Custom CSS class for the column
        render?: (value: any, row: T) => React.ReactNode;
        customSort?: CustomSortFunction<T>; // Custom sort function for this column
    }[];
    height: number; // Fixed height for virtualization
    rowHeight: number; // Height of each row
    
    // Header Section Props
    showHeader?: boolean; // Control header visibility
    headerContent?: React.ReactNode; // Custom header content
    headerClassName?: string; // Custom header styling
    headerBordered?: boolean; // Add borders to header
    
    // Body Section Props
    bodyClassName?: string; // Custom body styling
    bodyBordered?: boolean; // Add borders to body
    
    // Footer Section Props
    showFooter?: boolean; // Control footer visibility
    footerContent?: React.ReactNode; // Custom footer content
    footerClassName?: string; // Custom footer styling
    footerBordered?: boolean; // Add borders to footer
    
    // Table-wide Props
    bordered?: boolean; // Global border control
    className?: string; // Additional CSS classes for custom styling
    
    // Feature Props
    searchable?: boolean;
    searchPlaceholder?: string;
    filterable?: boolean;
    filters?: {
        key: string;
        label: string;
        value: string;
    }[];
    onFilterChange?: (filterKey: string, value: string) => void;
    activeFilter?: string;
    selectable?: boolean;
    onSelectionChange?: (selectedIds: string[]) => void;
    striped?: boolean;
    hoverable?: boolean;
    title?: string;
    description?: string;
    showRowNumbers?: boolean;
    onRowClick?: (row: T) => void;
    loading?: boolean;
    emptyState?: {
        icon?: string;
        title: string;
        description: string;
    };
    // Enhanced sorting props
    multiSort?: boolean; // Enable multi-column sorting
    defaultSort?: SortConfig[]; // Default sort configuration
    onSortChange?: (sortConfig: SortConfig[]) => void; // Sort change callback
    sortable?: boolean; // Global sortable toggle
    // Highlighting props
    highlightedRows?: (string | number)[]; // Array of row IDs to highlight
    highlightedColumns?: string[]; // Array of column keys to highlight
    highlightColor?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'; // Default highlight color
}

export function VirtualizedTable<T extends { id: string | number }>({
    data,
    columns,
    height,
    rowHeight,
    // Header Section Props
    showHeader = true,
    headerContent,
    headerClassName = '',
    headerBordered = true,
    // Body Section Props
    bodyClassName = '',
    bodyBordered = true,
    // Footer Section Props
    showFooter = true,
    footerContent,
    footerClassName = '',
    footerBordered = true,
    // Table-wide Props
    bordered = true,
    className = '',
    // Feature Props
    searchable = false,
    searchPlaceholder = "Search table...",
    filterable = false,
    filters = [],
    onFilterChange,
    activeFilter = 'all',
    selectable = false,
    onSelectionChange,
    striped = true,
    hoverable = true,
    title,
    description,
    showRowNumbers = false,
    onRowClick,
    loading = false,
    emptyState,
    // Enhanced sorting props
    multiSort = false,
    defaultSort = [],
    onSortChange,
    sortable = true,
    // Highlighting props
    highlightedRows = [],
    highlightedColumns = [],
    highlightColor = 'primary'
}: VirtualizedTableProps<T>) {
    // State for table logic
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [sortConfigs, setSortConfigs] = useState<SortConfig[]>(defaultSort || []);
    const [visibleColumns, setVisibleColumns] = useState<Set<any>>(new Set(columns.map(col => col.key)));
    const [searchTerm, setSearchTerm] = useState('');
    const [scrollTop, setScrollTop] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const headerScrollRef = useRef<HTMLDivElement>(null);

    // Filter data based on search and active filter
    const filteredData = useMemo(() => {
        let filtered = data;
        
        // Apply category filter if specified
        if (filterable && activeFilter && activeFilter !== 'all') {
            filtered = filtered.filter(row => {
                const categoryValue = (row as any)['category'];
                return categoryValue === activeFilter;
            });
        }
        
        // Apply search filter
        if (searchTerm) {
            console.log('🔍 Searching for:', searchTerm, 'in', data.length, 'rows');
            filtered = filtered.filter(row => 
                columns.some(column => {
                    const value = (row as any)[column.key];
                    const matches = String(value).toLowerCase().includes(searchTerm.toLowerCase());
                    if (matches) {
                        console.log('✅ Match found in column', column.key, ':', value);
                    }
                    return matches;
                })
            );
            console.log('🔍 Search results:', filtered.length, 'rows remaining');
        }
        
        return filtered;
    }, [data, columns, searchTerm, filterable, activeFilter]);

    // Enhanced sorting logic with multi-column support
    const sortedData = useMemo(() => {
        if (!sortConfigs.length) return filteredData;
        
        return [...filteredData].sort((a, b) => {
            // Sort by priority (1 = primary, 2 = secondary, 3 = tertiary)
            for (const sortConfig of sortConfigs.sort((x, y) => x.priority - y.priority)) {
                const column = columns.find(col => col.key === sortConfig.key);
                if (!column) continue;
                
                const valA = (a as any)[sortConfig.key];
                const valB = (b as any)[sortConfig.key];
                
                let comparison = 0;
                
                // Use custom sort function if provided
                if (column.customSort) {
                    comparison = column.customSort(a, b, sortConfig.direction);
                } else {
                    // Default sorting logic
                    if (typeof valA === 'string' && typeof valB === 'string') {
                        comparison = valA.localeCompare(valB);
                    } else if (typeof valA === 'number' && typeof valB === 'number') {
                        comparison = valA - valB;
                    } else {
                        comparison = String(valA).localeCompare(String(valB));
                    }
                }
                
                // Apply direction
                if (sortConfig.direction === 'desc') {
                    comparison = -comparison;
                }
                
                // If this level comparison is not equal, return it
                if (comparison !== 0) {
                    return comparison;
                }
            }
            
            return 0; // All sort levels are equal
        });
    }, [filteredData, sortConfigs, columns]);

    // Virtualization calculations
    const totalHeight = sortedData.length * rowHeight;
    const visibleRows = Math.ceil(height / rowHeight) + 2; // Buffer rows
    const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight));
    const endIndex = Math.min(sortedData.length, startIndex + visibleRows);
    
    // Calculate visible data for virtualization
    const visibleData = useMemo(() => {
        const start = startIndex;
        const end = Math.min(start + Math.ceil(height / rowHeight), sortedData.length);
        return sortedData.slice(start, end);
    }, [sortedData, startIndex, height, rowHeight]);
    const offsetY = startIndex * rowHeight;

    // Calculate total width needed for all columns
    const visibleColumnsArray = columns.filter(col => visibleColumns.has(col.key));
    const totalTableWidth = useMemo(() => {
        let width = 0;
        if (selectable) width += 16; // Checkbox column width
        if (showRowNumbers) width += 64; // Row number column width
        visibleColumnsArray.forEach(column => {
            width += (column.width || 150); // Default width if not specified
        });
        return width;
    }, [visibleColumnsArray, selectable, showRowNumbers]);

    // Handle vertical scroll
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop);
    }, []);

    // Handle horizontal scroll synchronization
    const handleHeaderScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const scrollLeft = e.currentTarget.scrollLeft;
        // setScrollLeft(scrollLeft); // Removed scrollLeft state
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollLeft;
        }
    }, []);

    const handleBodyScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        // Handle vertical scroll for virtualization
        setScrollTop(e.currentTarget.scrollTop);
        
        // Handle horizontal scroll synchronization with header
        const scrollLeft = e.currentTarget.scrollLeft;
        // setScrollLeft(scrollLeft); // Removed scrollLeft state
        if (headerScrollRef.current) {
            headerScrollRef.current.scrollLeft = scrollLeft;
        }
    }, []);

    // Enhanced sort handling with multi-column support
    const handleSort = useCallback((columnKey: any, direction: 'asc' | 'desc') => {
        if (!sortable) return;
        
        let newSortConfigs: SortConfig[];
        
        if (multiSort) {
            // Multi-column sorting: add or update sort config
            const existingIndex = sortConfigs.findIndex(config => config.key === columnKey);
            
            if (existingIndex >= 0) {
                // Update existing sort config
                newSortConfigs = [...sortConfigs];
                if (direction === sortConfigs[existingIndex].direction) {
                    // Same direction, remove this sort level
                    newSortConfigs.splice(existingIndex, 1);
                } else {
                    // Different direction, update it
                    newSortConfigs[existingIndex] = { ...newSortConfigs[existingIndex], direction };
                }
            } else {
                // Add new sort config with next priority
                const nextPriority = sortConfigs.length + 1;
                if (nextPriority <= 3) { // Max 3 sort levels
                    newSortConfigs = [...sortConfigs, { key: columnKey, direction, priority: nextPriority }];
                } else {
                    // Replace lowest priority sort
                    newSortConfigs = [...sortConfigs.slice(1), { key: columnKey, direction, priority: 3 }];
                }
            }
        } else {
            // Single-column sorting: replace all sorts with this one
            newSortConfigs = [{ key: columnKey, direction, priority: 1 }];
        }
        
        setSortConfigs(newSortConfigs);
        onSortChange?.(newSortConfigs);
    }, [sortable, multiSort, sortConfigs, onSortChange]);

    // Handle selection
    const handleSelectAll = useCallback((checked: boolean) => {
        const newSelectedIds = checked ? visibleData.map(row => String(row.id)) : [];
        setSelectedIds(newSelectedIds);
        onSelectionChange?.(newSelectedIds);
    }, [visibleData, onSelectionChange]);

    const handleSelectRow = useCallback((id: string, checked: boolean) => {
        const newSelectedIds = checked 
            ? [...selectedIds, id]
            : selectedIds.filter(selectedId => selectedId !== id);
        setSelectedIds(newSelectedIds);
        onSelectionChange?.(newSelectedIds);
    }, [selectedIds, onSelectionChange]);

    // Column visibility
    const toggleColumnVisibility = useCallback((columnKey: any) => {
        const newVisibleColumns = new Set(visibleColumns);
        if (newVisibleColumns.has(columnKey)) {
            newVisibleColumns.delete(columnKey);
        } else {
            newVisibleColumns.add(columnKey);
        }
        setVisibleColumns(newVisibleColumns);
    }, [visibleColumns]);

    // Scroll to top when data changes
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
            setScrollTop(0);
        }
    }, [data.length, searchTerm, activeFilter]);

    if (loading) {
        return (
            <div className={`space-y-4 ${className}`}>
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-orange mx-auto mb-2"></div>
                        <p className="text-text-secondary dark:text-dark-text-secondary">Loading data...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Header */}
            {showHeader && (
                <div className={`${headerBordered ? 'border-b border-border-color dark:border-dark-border-color' : ''} ${headerClassName}`}>
                    {headerContent || (
                        <>
                            {(title || description) && (
                                <div className="mb-6">
                                    {title && <h3 className="text-lg font-semibold text-text-primary dark:text-dark-text-primary">{title}</h3>}
                                    {description && <p className="text-sm text-text-secondary dark:text-dark-text-secondary mt-1">{description}</p>}
                                </div>
                            )}

                            {/* Controls Bar */}
                            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                    {/* Search */}
                                    {searchable && (
                                        <div className="w-full sm:w-80">
                                            <div className="relative">
                                                <input
                                                    id="virtualized-table-search"
                                                    type="text"
                                                    placeholder={searchPlaceholder || "Search table..."}
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="w-full pl-4 pr-10 py-2 text-sm bg-bg-tertiary dark:bg-dark-bg-primary rounded-lg border border-border-color dark:border-dark-border-color focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent placeholder:text-text-secondary/60 dark:placeholder:text-dark-text-secondary/60"
                                                />
                                                <Icon className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary/60 pointer-events-none">
                                                    search
                                                </Icon>
                                            </div>
                                        </div>
                                    )}

                                    {/* Filters */}
                                    {filterable && filters.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">Filter:</span>
                                            <div className="flex flex-wrap gap-2">
                                                {filters.map(filter => (
                                                    <Button
                                                        key={filter.key}
                                                        variant={activeFilter === filter.value ? 'primary' : 'ghost'}
                                                        size="sm"
                                                        onClick={() => onFilterChange?.(filter.key, filter.value)}
                                                        className="whitespace-nowrap"
                                                    >
                                                        {filter.label}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                    {/* Column Visibility */}
                                    <Dropdown>
                                        <Dropdown.Trigger>
                                            <Button variant="outline" size="sm" className="whitespace-nowrap">
                                                <Icon>view_column</Icon>
                                                <span className="hidden sm:inline">Columns</span>
                                            </Button>
                                        </Dropdown.Trigger>
                                        <Dropdown.Content>
                                            {columns.map(column => (
                                                <Dropdown.Item
                                                    key={String(column.key)}
                                                    onSelect={() => toggleColumnVisibility(column.key)}
                                                    className="flex items-center gap-2"
                                                >
                                                    <RawCheckbox
                                                        id={`col-${String(column.key)}`}
                                                        checked={visibleColumns.has(column.key)}
                                                        onChange={() => {}}
                                                    />
                                                    {column.header}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Content>
                                    </Dropdown>

                                    {/* Selection Info */}
                                    {selectable && (
                                        <div className="flex items-center gap-2">
                                            <RawCheckbox
                                                id="select-all-virtualized"
                                                checked={selectedIds.length === visibleData.length && visibleData.length > 0}
                                                indeterminate={selectedIds.length > 0 && selectedIds.length < visibleData.length}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                            />
                                            <span className="text-sm text-text-secondary dark:text-dark-text-secondary whitespace-nowrap">
                                                {selectedIds.length} of {sortedData.length} selected
                                            </span>
                                        </div>
                                    )}

                                    {/* Virtualization Info */}
                                    <div className="px-3 py-1 rounded-full bg-brand-orange/10 dark:bg-dark-brand-orange/10 text-xs font-medium text-brand-orange dark:text-dark-brand-orange whitespace-nowrap">
                                        Virtualized ({visibleData.length}/{sortedData.length})
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Virtualized Table Container */}
            <div 
                ref={containerRef}
                className={`border border-border-color dark:border-dark-border-color rounded-lg overflow-hidden ${bordered ? 'border-t-0' : ''}`}
                style={{ height }}
            >
                {/* Table Header - Always visible for controls and context */}
                {showHeader && (
                    <div 
                        ref={headerScrollRef}
                        className={`bg-bg-tertiary dark:bg-dark-bg-tertiary border-b border-border-color dark:border-dark-border-color overflow-x-auto custom-scrollbar ${headerBordered ? 'border-b border-border-color dark:border-dark-border-color' : ''} ${headerClassName}`}
                        onScroll={handleHeaderScroll}
                    >
                        <div className="flex" style={{ minWidth: totalTableWidth }}>
                            {selectable && (
                                <div className="px-8 py-4 w-4 flex items-center flex-shrink-0">
                                    <RawCheckbox
                                        id="header-select-all-virtualized"
                                        checked={selectedIds.length === visibleData.length && visibleData.length > 0}
                                        indeterminate={selectedIds.length > 0 && selectedIds.length < visibleData.length}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                    />
                                </div>
                            )}
                            {showRowNumbers && (
                                <div className="px-8 py-4 w-16 flex items-center flex-shrink-0">
                                    <span className="font-bold text-sm text-text-primary dark:text-dark-text-primary uppercase">#</span>
                                </div>
                            )}
                            {visibleColumnsArray.map(column => (
                                <div 
                                    key={String(column.key)} 
                                    className={`px-8 py-4 text-left font-bold text-sm text-text-primary dark:text-dark-text-primary uppercase flex-shrink-0 whitespace-nowrap ${
                                        column.sortable ? 'cursor-pointer hover:bg-bg-tertiary/50 dark:hover:bg-dark-bg-tertiary/50 transition-colors' : ''
                                    } ${column.className || ''}`}
                                    style={{ width: column.width }}
                                >
                                    <button className="flex items-center gap-2 font-bold group" onClick={() => {
                                        if (column.sortable) {
                                            const existingSort = sortConfigs.find(config => config.key === column.key);
                                            const newDirection = existingSort?.direction === 'asc' ? 'desc' : 'asc';
                                            handleSort(column.key, newDirection);
                                        }
                                    }}>
                                        <span className="font-bold text-sm text-text-primary dark:text-dark-text-primary uppercase whitespace-nowrap">{column.header}</span>
                                        {column.sortable && (
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                {sortConfigs.map((sortConfig, index) => {
                                                    if (sortConfig.key === column.key) {
                                                        return (
                                                            <div key={index} className="flex items-center gap-1">
                                                                <Icon className="!text-base text-brand-navy dark:text-dark-text-primary">
                                                                    {sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                                                                </Icon>
                                                                {multiSort && sortConfig.priority > 1 && (
                                                                    <span className="text-sm text-brand-navy dark:text-dark-text-primary font-bold">
                                                                        {sortConfig.priority}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                {!sortConfigs.some(config => config.key === column.key) && (
                                                    <Icon className="!text-base opacity-0 group-hover:opacity-50 transition-opacity">
                                                        unfold_more
                                                    </Icon>
                                                )}
                                            </div>
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Table Body - Shows data or empty state, always maintains structure */}
                <div 
                    ref={scrollRef}
                    className={`overflow-x-auto custom-scrollbar ${bodyBordered ? 'border-b border-border-color dark:border-dark-border-color' : ''} ${bodyClassName}`}
                    style={{ height: showHeader ? height - 60 : height }}
                    onScroll={handleBodyScroll}
                >
                    {(() => {
                        console.log('🔍 Render check:', { 
                            sortedDataLength: sortedData.length, 
                            searchTerm, 
                            activeFilter,
                            dataLength: data.length 
                        });
                        return sortedData.length > 0 ? (
                            // Show data when available
                            <div style={{ height: totalHeight, position: 'relative', minWidth: totalTableWidth }}>
                                <div style={{ transform: `translateY(${offsetY}px)` }}>
                                    {visibleData.map((row, index) => (
                                        <div
                                            key={row.id}
                                            className={`flex items-center border-b border-border-color dark:border-dark-border-color transition-colors ${
                                                striped && (startIndex + index) % 2 === 0 ? 'bg-bg-tertiary/50 dark:bg-dark-bg-tertiary/30' : 'bg-bg-secondary dark:bg-dark-bg-secondary'
                                            } ${hoverable ? 'hover:bg-bg-tertiary/80 dark:hover:bg-dark-bg-tertiary/60' : ''} ${
                                                selectedIds.includes(String(row.id)) ? '!bg-brand-orange/10 dark:!bg-brand-orange/10' : ''
                                            } ${
                                                highlightedRows.includes(row.id) ? {
                                                    primary: '!bg-brand-orange/10 dark:!bg-dark-brand-orange/10',
                                                    secondary: '!bg-brand-navy/10 dark:!bg-dark-brand-navy/10',
                                                    success: '!bg-system-success-light dark:!bg-system-success-dark-bg/40',
                                                    warning: '!bg-system-warning-light dark:!bg-system-warning-dark-bg/40',
                                                    error: '!bg-system-error-light dark:!bg-system-error-dark-bg/40',
                                                    info: '!bg-system-info-light dark:!bg-system-info-dark-bg/40'
                                                }[highlightColor] : ''
                                            } ${onRowClick ? 'cursor-pointer' : ''}`}
                                            style={{ height: rowHeight }}
                                            onClick={onRowClick ? () => onRowClick(row) : undefined}
                                        >
                                            {selectable && (
                                                <div className="px-8 py-4 w-4 flex items-center flex-shrink-0">
                                                    <RawCheckbox
                                                        id={`row-${row.id}`}
                                                        checked={selectedIds.includes(String(row.id))}
                                                        onChange={(e) => handleSelectRow(String(row.id), e.target.checked)}
                                                    />
                                                </div>
                                            )}
                                            {showRowNumbers && (
                                                <div className="px-8 py-4 w-16 text-sm text-text-secondary dark:text-dark-text-secondary font-mono flex items-center flex-shrink-0">
                                                    <span>{startIndex + index + 1}</span>
                                                </div>
                                            )}
                                            {visibleColumnsArray.map(column => {
                                                // Check if this column should be highlighted
                                                const isColumnHighlighted = highlightedColumns.includes(column.key);
                                                const columnHighlightClass = isColumnHighlighted ? {
                                                    primary: '!bg-brand-orange/10 dark:!bg-dark-brand-orange/10',
                                                    secondary: '!bg-brand-navy/10 dark:!bg-dark-brand-navy/10',
                                                    success: '!bg-system-success-light dark:!bg-system-success-dark-bg/40',
                                                    warning: '!bg-system-warning-light dark:!bg-system-warning-dark-bg/40',
                                                    error: '!bg-system-error-light dark:!bg-system-error-dark-bg/40',
                                                    info: '!bg-system-info-light dark:!bg-system-info-dark-bg/40'
                                                }[highlightColor] : '';
                                                
                                                return (
                                                <div 
                                                    key={String(column.key)} 
                                                    className={`px-8 py-4 text-sm text-text-secondary dark:text-dark-text-secondary flex items-center flex-shrink-0 ${
                                                        column.textWrap === 'wrap' ? 'break-words' : column.textWrap === 'nowrap' ? 'whitespace-nowrap' : 'truncate'
                                                    } ${columnHighlightClass} ${column.className || ''}`}
                                                    style={{ width: column.width }}
                                                >
                                                    <span className={`w-full ${
                                                        column.textWrap === 'wrap' ? 'break-words' : column.textWrap === 'nowrap' ? 'whitespace-nowrap' : 'truncate'
                                                    }`}>
                                                        {column.render 
                                                            ? column.render((row as any)[column.key], row)
                                                            : String((row as any)[column.key])
                                                        }
                                                    </span>
                                                </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            // Show empty state when no data, but maintain table structure
                            <div className="flex items-center justify-center h-full min-h-[200px] bg-bg-secondary dark:bg-dark-bg-secondary">
                                <div className="text-center">
                                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-bg-tertiary dark:bg-dark-bg-tertiary">
                                        <Icon className="text-2xl text-text-secondary dark:text-dark-text-secondary">
                                            {emptyState?.icon || 'search_off'}
                                        </Icon>
                                    </div>
                                    <h3 className="text-lg font-semibold text-text-primary dark:text-dark-text-primary mb-2">
                                        {emptyState?.title || 'No Data Found'}
                                    </h3>
                                    <p className="text-sm text-text-secondary dark:text-dark-text-secondary mb-4 max-w-md">
                                        {emptyState?.description || 
                                            (searchTerm 
                                                ? `No results found for "${searchTerm}". Try adjusting your search terms or filters.`
                                                : 'No data available. Please check your filters or try a different search.'
                                            )
                                        }
                                    </p>
                                    {searchTerm && (
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            className="text-sm text-brand-orange hover:text-brand-orange/80 transition-colors font-medium"
                                        >
                                            Clear search
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Footer Info - Always visible for context and actions */}
            {showFooter && (
                <div className={`flex items-center justify-between text-sm text-text-secondary dark:text-dark-text-secondary ${footerBordered ? 'border-t border-border-color dark:border-dark-border-color' : ''} ${footerClassName}`}>
                    <div className="flex items-center gap-4">
                        {sortedData.length > 0 ? (
                            <span>
                                Showing {startIndex + 1} to {endIndex} of {sortedData.length} results
                                {searchTerm && ` (filtered from ${data.length} total)`}
                            </span>
                        ) : (
                            <span>
                                {searchTerm 
                                    ? `No results found for "${searchTerm}"`
                                    : 'No data available'
                                }
                                {searchTerm && ` (from ${data.length} total items)`}
                            </span>
                        )}
                        {sortConfigs.length > 0 && (
                            <button
                                onClick={() => {
                                    setSortConfigs([]);
                                    onSortChange?.([]);
                                }}
                                className="text-xs text-brand-orange hover:text-brand-orange/80 transition-colors"
                            >
                                Clear sorting
                            </button>
                        )}
                        {sortedData.length === 0 && searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="text-xs text-brand-orange hover:text-brand-orange/80 transition-colors"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                    <span className="text-xs">
                        Row height: {rowHeight}px • Container: {height}px • Performance optimized
                        {multiSort && sortConfigs.length > 0 && ` • ${sortConfigs.length} sort level${sortConfigs.length > 1 ? 's' : ''}`}
                        {sortedData.length === 0 && ` • No data to display`}
                    </span>
                </div>
            )}
        </div>
    );
}